import { Point } from './Point';

/*
 * Rect
 */
export class Rect {
  private _x: number;
  private _y: number;
  private _width: number;
  private _height: number;

  get x() {
    return this._x;
  }

  get y() {
    return this._y;
  }

  get width() {
    return this._width;
  }

  get height() {
    return this._height;
  }

  private constructor(
    x = 0,
    y = 0,
    width = 0,
    height = 0,
  ) {
    this._x = x;
    this._y = y;
    this._width = width;
    this._height = height;
  }

  setY(y: number) {
    this._y = y;
  }

  moveY(y: number) {
    this._y += y;
  }

  setX(x: number) {
    this._x = x;
  }

  moveX(x: number) {
    this._x += x;
  }

  setHeight(height: number) {
    this._height = height;
  }

  setWidth(width: number) {
    this._width = width;
  }

  static createEmpty() {
    return new Rect();
  }

  static create(x: number, y: number, width: number, height: number) {
    return new Rect(x, y, width, height);
  }

  clone() {
    return new Rect(this.x, this.y, this.width, this.height);
  }

  // convenience method
  get right() {
    return this.x + this.width;
  }

  get bottom() {
    return this.y + this.height;
  }

  equals(rect: Rect) {
    return this.x === rect.x && this.y === rect.y &&
      this.width === rect.width && this.height === rect.height;
  }

  // other can be a Point or a Rect
  contains(other: Point | Rect): boolean {
    if (other instanceof Point) {
      return other.x >= this.x &&
        other.x <= this.x + this.width &&
        other.y >= this.y &&
        other.y <= this.y + this.height;
    } else {
      return this.x <= other.x &&
        this.x + this.width >= other.x + other.width &&
        this.y <= other.y &&
        this.y + this.height >= other.y + other.height;
    }
  }

  horizontalUnion(rect: Rect): Rect {
    const right = Math.max(this.x + this.width, rect.x + rect.width);
    const newX = Math.min(this.x, rect.x);

    return new Rect(
      newX,
      this.y,
      right - newX,
      this.height);
  }

  union(rect: Rect): Rect {
    const right = Math.max(this.x + this.width, rect.x + rect.width);
    const bottom = Math.max(this.y + this.height, rect.y + rect.height);

    const newX = Math.min(this.x, rect.x);
    const newY = Math.min(this.y, rect.y);

    return new Rect(
      newX,
      newY,
      right - newX,
      bottom - newY);
  }

  isEmpty() {
    return (this.x === Infinity &&
            this.y === Infinity &&
            this.width === -Infinity &&
            this.height === -Infinity);
  }
}
